import React, { useEffect, useState } from 'react';
import {
    AccountCard,
    AccountGrid,
    AccountSection,
    SectionBadge,
    inputClassName,
    labelClassName,
    primaryButtonClassName,
    readOnlyInputClassName,
    secondaryButtonClassName,
} from './AccountUI';

const CameraIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </svg>
);

const SpinnerIcon = ({ className = '' }) => (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.37 0 0 5.37 0 12h4zm2 5.29A7.96 7.96 0 0 1 4 12H0c0 3.04 1.14 5.82 3 7.94l3-2.65z" />
    </svg>
);

const Notification = ({ message, type }) => {
    if (!message) {
        return null;
    }

    const tones = type === 'success'
        ? 'border-blue-200 bg-blue-50 text-blue-700'
        : 'border-red-200 bg-red-50 text-red-700';

    return (
        <div className={`rounded-2xl border px-4 py-3 text-sm font-medium ${tones}`}>
            {message}
        </div>
    );
};

const syncProfileSnapshot = (profile) => {
    const snapshot = {
        name: profile.nickname || '',
        nickname: profile.nickname || '',
        email: profile.email || '',
        organizationName: profile.organizationName || '',
        organization_name: profile.organizationName || '',
        profilePicture: profile.profilePicture || '',
        profile_picture: profile.profilePicture || '',
    };

    localStorage.setItem('nickname', snapshot.nickname);
    localStorage.setItem('email', snapshot.email);
    localStorage.setItem('organizationName', snapshot.organizationName);
    localStorage.setItem('profile_picture', snapshot.profile_picture);

    window.dispatchEvent(new CustomEvent('roboman:user-profile-updated', { detail: snapshot }));
};

const ProfileIdentityAvatar = ({ name, email, src }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const fallbackLabel = (name || email || 'R').trim();
    const fallbackInitial = fallbackLabel ? fallbackLabel.charAt(0).toUpperCase() : 'R';

    useEffect(() => {
        setImageFailed(false);
    }, [src]);

    if (src && !imageFailed) {
        return (
            <img
                src={src}
                alt={name || email || 'Profile'}
                className="h-24 w-24 rounded-[28px] border border-blue-100 object-cover shadow-sm"
                onError={() => setImageFailed(true)}
            />
        );
    }

    return (
        <div className="flex h-24 w-24 items-center justify-center rounded-[28px] bg-blue-600 text-3xl font-semibold text-white">
            {fallbackInitial}
        </div>
    );
};

export default function UpdateProfile() {
    const roboman_api = process.env.REACT_APP_ROBOMAN_API || 'https://api.roboman.ai';
    const access_token = localStorage.getItem('access_token');
    const USER_TYPES = ['Business', 'Individual'];
    const COMPANY_TYPES = ['Partnership', 'Limited Liability Partnership (LLP)', 'Private Limited Company (Ltd)', 'Public Limited Company (PLC)', 'Umbrella Company'];

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({ message: null, type: '' });
    const [userData, setUserData] = useState({
        nickname: '',
        email: '',
        organizationName: '',
        profilePicture: '',
        userType: '',
        linkedinUrl: '',
        companyType: '',
        calendly: '',
        linkedin_connection_status: '',
    });

    const showNotification = (message, type = 'error') => {
        setNotification({ message, type });
        window.clearTimeout(window.__accountNotificationTimer);
        window.__accountNotificationTimer = window.setTimeout(() => {
            setNotification({ message: null, type: '' });
        }, 5000);
    };

    useEffect(() => {
        const fetchUserDataFromAPI = async () => {
            try {
                const response = await fetch(`${roboman_api}/users/me`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', Authorization: access_token },
                });

                if (!response.ok) {
                    showNotification('This account does not have any information.', 'error');
                    return;
                }

                const data = await response.json();
                const nextUserData = {
                    nickname: data.user_info?.nick_name || '',
                    email: data.email || '',
                    organizationName: data.user_info?.organization_name || '',
                    profilePicture: data.profile_picture || '',
                    userType: data.user_info?.user_type || 'Individual',
                    linkedinUrl: data.user_info?.linkedin_address || '',
                    companyType: data.user_info?.company_type || 'Partnership',
                    calendly: data.user_info?.calendly || '',
                    linkedin_connection_status: data.linkedin_connection_status || 'NOT_CONNECTED',
                };
                setUserData(nextUserData);
                syncProfileSnapshot(nextUserData);
            } catch (error) {
                console.error('Error fetching user data:', error);
                showNotification('Could not retrieve user information. Please try again.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        if (access_token) {
            fetchUserDataFromAPI();
        } else {
            setIsLoading(false);
            showNotification('Authentication token not found.', 'error');
        }
    }, [access_token, roboman_api]);

    const handleInputChange = (field, value) => {
        setUserData((previous) => ({ ...previous, [field]: value }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                nick_name: userData.nickname,
                organization_name: userData.organizationName,
                user_type: userData.userType,
                billing_plan: 'None',
                company_type: userData.companyType,
                linkedin_address: userData.linkedinUrl,
                calendly: userData.calendly,
            };

            const response = await fetch(`${roboman_api}/users/me/update-info`, {
                method: 'PUT',
                headers: {
                    accept: 'application/json',
                    Authorization: access_token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (result.status === 'success') {
                syncProfileSnapshot(userData);
                showNotification('Your profile details were updated.', 'success');
                return;
            }

            throw new Error(result.message || 'There was an issue updating your account.');
        } catch (error) {
            showNotification(error.message || 'An unexpected error occurred.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="p-8 sm:p-10">
                <div className="flex items-center gap-3 text-sm text-slate-500">
                    <SpinnerIcon className="h-5 w-5 text-slate-400" />
                    Loading profile details...
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 sm:p-8 lg:p-10">
            <AccountSection
                eyebrow="Profile"
                title=""
                subtitle="Keep the essentials current so your workspace, campaigns, and connected tools stay aligned."
                actions={<SectionBadge tone={userData.linkedin_connection_status === 'CONNECTED' ? 'blue' : 'slate'}>{userData.linkedin_connection_status === 'CONNECTED' ? 'LinkedIn Connected' : 'LinkedIn Not Connected'}</SectionBadge>}
            >
                <Notification message={notification.message} type={notification.type} />

                <AccountGrid className="items-start">
                    <AccountCard className="xl:col-span-4 self-start">
                        <div className="flex flex-col items-start gap-5">
                            <div className="relative">
                                <ProfileIdentityAvatar
                                    name={userData.nickname}
                                    email={userData.email}
                                    src={userData.profilePicture}
                                />
                                <div className="absolute -bottom-2 -right-2 flex h-10 w-10 items-center justify-center rounded-2xl border border-blue-100 bg-white text-blue-600 shadow-sm">
                                    <CameraIcon className="h-4 w-4" />
                                </div>
                            </div>
                            <div className="min-w-0 space-y-2">
                                <p className="text-sm text-slate-500">Workspace identity</p>
                                <h3 className="break-words text-xl font-semibold text-slate-950">{userData.nickname || 'Unnamed profile'}</h3>
                                <p className="break-all text-sm text-slate-500">{userData.email}</p>
                                {userData.organizationName ? <p className="break-words text-sm font-medium text-blue-600">{userData.organizationName}</p> : null}
                            </div>
                        </div>
                    </AccountCard>

                    <AccountCard className="xl:col-span-8 self-start">
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            <label className="block">
                                <span className={labelClassName}>Full name</span>
                                <input type="text" value={userData.nickname} onChange={(event) => handleInputChange('nickname', event.target.value)} className={inputClassName} />
                            </label>
                            <label className="block">
                                <span className={labelClassName}>Email address</span>
                                <input type="email" value={userData.email} disabled className={readOnlyInputClassName} />
                            </label>
                            <label className="block">
                                <span className={labelClassName}>Organization</span>
                                <input type="text" value={userData.organizationName} onChange={(event) => handleInputChange('organizationName', event.target.value)} className={inputClassName} placeholder="Detected from your work email when available" />
                            </label>
                            <label className="block">
                                <span className={labelClassName}>User type</span>
                                <select value={userData.userType} onChange={(event) => handleInputChange('userType', event.target.value)} className={inputClassName}>
                                    {USER_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className={labelClassName}>Company type</span>
                                <select value={userData.companyType} onChange={(event) => handleInputChange('companyType', event.target.value)} className={inputClassName}>
                                    {COMPANY_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                                </select>
                            </label>
                            <label className="block md:col-span-2">
                                <span className={labelClassName}>LinkedIn profile URL</span>
                                <input type="text" value={userData.linkedinUrl} onChange={(event) => handleInputChange('linkedinUrl', event.target.value)} className={inputClassName} placeholder="https://linkedin.com/in/your-profile" />
                            </label>
                        </div>
                    </AccountCard>
                </AccountGrid>

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                    <button type="button" className={secondaryButtonClassName} onClick={() => window.location.reload()}>
                        Reset
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={isSubmitting} className={primaryButtonClassName}>
                        {isSubmitting ? <span className="flex items-center gap-2"><SpinnerIcon className="h-4 w-4" /> Saving...</span> : 'Save profile'}
                    </button>
                </div>
            </AccountSection>
        </div>
    );
}
